import { Injectable } from '@angular/core';
import zxcvbn from 'zxcvbn';

export interface PasswordValidationResult {
    isValid: boolean;
    score: number;
    hasMinimumLength: boolean;
    hasMinimumStrength: boolean;
    feedback?: string[];
}

@Injectable({
    providedIn: 'root',
})
export class PasswordValidationService {
    readonly MIN_LENGTH = 10;
    readonly MIN_STRENGTH_SCORE = 3; // zxcvbn scores are 0-4, we require 3+

    /**
     * Validates a password using zxcvbn strength estimation
     * @param password The password to validate
     * @returns Validation result with score and feedback
     */
    validatePassword(password: string | null | undefined): PasswordValidationResult {
        if (!password) {
            return {
                isValid: false,
                score: 0,
                hasMinimumLength: false,
                hasMinimumStrength: false,
            };
        }

        const hasMinimumLength = password.length >= this.MIN_LENGTH;
        const result = zxcvbn(password);
        const hasMinimumStrength = result.score >= this.MIN_STRENGTH_SCORE;

        const isValid = hasMinimumLength && hasMinimumStrength;

        return {
            isValid,
            score: result.score,
            hasMinimumLength,
            hasMinimumStrength,
            feedback: result.feedback.suggestions.length > 0 ? result.feedback.suggestions : undefined,
        };
    }

    /**
     * Gets the strength label for display
     * @param score zxcvbn score (0-4)
     * @returns Human-readable strength label
     */
    getStrengthLabel(score: number): string {
        const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
        return labels[score] || 'Unknown';
    }
}
